/**
 * RSI_ZONES — Overbought / Oversold background bands for the RSI sub-pane
 *
 * Stacked companion to the native 'RSI' indicator (same pattern as
 * VOL_LABELS/CROSS_VOL stacked onto vol_pane). Draws two static translucent
 * bands across the full pane width: reddish above the 70 level (overbought),
 * greenish below the 30 level (oversold). No per-bar computation — the bands
 * don't depend on the current RSI value, just the fixed 30/70 thresholds.
 */

import { IndicatorSeries, type IndicatorTemplate } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';

type DrawParams = {
  ctx:      CanvasRenderingContext2D;
  bounding: { width: number; height: number };
  yAxis:    { convertToPixel: (v: number) => number };
};

const OVERBOUGHT_COLOR = 'rgba(239,83,80,0.08)';  // red-ish
const OVERSOLD_COLOR   = 'rgba(38,166,154,0.08)'; // green-ish

const rsiZones: IndicatorTemplate = {
  name:       'RSI_ZONES',
  shortName:  'RSI Zones',
  series:     IndicatorSeries.Normal,
  shouldOhlc: false,
  figures:    [], // all rendering is manual
  // Force the RSI pane's shared y-axis to always span at least [0, 100].
  // Without this, klinecharts auto-fits the axis tightly to the *visible*
  // RSI value range (e.g. 35-65) — whenever RSI never actually reaches
  // 70/30 within the visible window, convertToPixel(70/30) lands outside
  // the pane and the bands draw with zero height (invisible even with the
  // toggle on). minValue/maxValue are unioned across every indicator
  // sharing the pane when klinecharts computes the axis range (see
  // YAxisImp.calcRange in klinecharts' dist bundle), so this alone fixes
  // it for the whole pane — RSI's own line and RSI_DIVERGENCE included.
  minValue: 0,
  maxValue: 100,

  calc: (dataList) => dataList.map(() => ({})),

  draw: (params: unknown): boolean => {
    if (!useChartStore.getState().rsiZonesEnabled) return true;

    const { ctx, bounding, yAxis } = params as DrawParams;
    const y70 = yAxis.convertToPixel(70);
    const y30 = yAxis.convertToPixel(30);

    ctx.save();
    ctx.fillStyle = OVERBOUGHT_COLOR;
    ctx.fillRect(0, 0, bounding.width, Math.max(0, y70));

    ctx.fillStyle = OVERSOLD_COLOR;
    ctx.fillRect(0, y30, bounding.width, Math.max(0, bounding.height - y30));
    ctx.restore();

    return true; // suppress default figure rendering
  },
};

export default rsiZones;
